import { InvoiceStatus, Maybe } from '@bloomays-lib/types.shared';
import { GridRenderCellParams } from '@mui/x-data-grid-premium';
import IconButton from '@mui/material/IconButton';
import get from 'lodash/fp/get';

const renderIconLabel = (label: string, emoji?: string, onClick?: () => void) => {
  return (
    <IconButton size="small" onClick={onClick} title={label}>
      {emoji || label}
    </IconButton>
  ) as React.ReactNode;
};

// 'Non créée' | 'En erreur' | 'Créée à valider' | 'Créée et finalisée' ;
export const invoiceStatus: Record<InvoiceStatus, { value: InvoiceStatus; label: string }> = {
  'Non créée': { value: 'Non créée', label: '🚧 Non créée' },
  'En erreur': { value: 'En erreur', label: '🚨 En erreur' },
  'Créée à valider': { value: 'Créée à valider', label: '☑️ Créée à valider' },
  'Créée et finalisée': { value: 'Créée et finalisée', label: '✅ Créée et finalisée' },
  'En cours': { value: 'En cours', label: '🔄 En cours' },
  Annulée: { value: 'Annulée', label: '❌ Annulée' },
};

type ActivityInvoice = {
  bloomerInvoiceStatus: InvoiceStatus;
  bloomerInvoiceLastError: Maybe<string>;
  clientInvoiceStatus: InvoiceStatus;
  clientInvoiceLastError: Maybe<string>;
};

type BillingCustomerInvoice = {
  invoiceStatus: InvoiceStatus;
  lastCustomerInvoiceError?: string;
};

type InvoiceType = ActivityInvoice | BillingCustomerInvoice;

export const renderInvoiceStatusCell =
  <T extends InvoiceType>(
    rowColName: T extends ActivityInvoice ? 'bloomerInvoiceStatus' | 'clientInvoiceStatus' : 'invoiceStatus',
    rowErrorColName: T extends ActivityInvoice
      ? 'bloomerInvoiceLastError' | 'clientInvoiceLastError'
      : 'lastCustomerInvoiceError',
    couldBeCreated: (row: T) => boolean,
    clickHandler?: (row: T) => () => void,
  ) =>
  (params: GridRenderCellParams<T>): React.ReactNode => {
    // rendering for group header rows
    if (params.rowNode.type === 'group') {
      if (params.field !== '__row_group_by_columns_group__') return '';
      const groupingKey = params.rowNode.groupingKey as InvoiceStatus;
      if (!groupingKey) return;
      return invoiceStatus[groupingKey].label;
    }
    const rowValue = get(rowColName, params.row);
    const rowErrorValue = get(rowErrorColName, params.row);
    switch (rowValue) {
      case 'Annulée':
        return renderIconLabel('Annulée', '❌');
      case 'Non créée':
        if (couldBeCreated(params.row as T)) {
          return renderIconLabel('A créer', '🚧', clickHandler ? clickHandler(params.row as T) : undefined);
        }
        return renderIconLabel('Non créée', '🚧');
      case 'En erreur':
        return renderIconLabel(
          rowErrorValue || 'Contacter service technique',
          '🚨',
          clickHandler ? clickHandler(params.row as T) : undefined,
        );
      case 'Créée à valider':
        return renderIconLabel(
          `Créée à valider\n${rowErrorValue}`,
          '☑️',
          clickHandler ? clickHandler(params.row as T) : undefined,
        );
      case 'Créée et finalisée':
        return renderIconLabel('Créée et finalisée', '✅', clickHandler ? clickHandler(params.row as T) : undefined);
      case 'En cours':
        return renderIconLabel('En cours', '🔄');
      default:
        return '';
    }
  };
